import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { formatDateTimeKST } from '../utils/dateUtils';
import { KeywordSetting, KeywordSettingParams } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import Pagination from './Pagination';
import KeywordDataSidePanel from './KeywordDataSidePanel';

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.base};
  overflow: hidden;
`;

// 테이블 스타일
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

// 테이블 헤더 스타일
const TableHeader = styled.thead`
  background: ${colors.semantic.background.secondary};
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 헤더 셀 스타일
const HeaderCell = styled.th`
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.semantic.border.primary};
  
  &:last-child {
    border-right: none;
  }
`;

// 테이블 바디 스타일
const TableBody = styled.tbody``;

// 테이블 행 스타일
const TableRow = styled.tr`
  border-bottom: 1px solid ${colors.secondary[100]};

  &:hover {
    background: ${colors.semantic.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

// 데이터 셀 스타일
const DataCell = styled.td`
  padding: 16px 12px;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  border-right: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-right: none;
  }
`;

// 활성화 상태 배지 스타일
const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  font-weight: 500;

  ${props =>
    props.status === 'ON'
      ? `
    background: ${colors.success[100]};
    color: ${colors.success[800]};
  `
      : `
    background: ${colors.error[50]};
    color: ${colors.error[800]};
  `}
`;

// 분석유형 배지 스타일
const AnalysisTypeBadge = styled.span<{ type: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  font-weight: 500;

  ${props =>
    props.type === '자동'
      ? `
    background: ${colors.primary[100]};
    color: ${colors.primary[800]};
  `
      : `
    background: ${colors.secondary[100]};
    color: ${colors.secondary[700]};
  `}
`;

// 타겟사이트 배지 스타일
const SiteBadge = styled.span<{ site: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  font-weight: 500;
  background: ${colors.secondary[100]};
  color: ${colors.semantic.text.secondary};

  ${props => {
    switch (props.site) {
      case '네이버카페':
        return `background: ${colors.primary[100]}; color: ${colors.primary[700]};`;
      case '네이버블로그':
        return `background: ${colors.primary[100]}; color: ${colors.primary[700]};`;
      case '디시인사이드':
        return `background: ${colors.warning[100]}; color: ${colors.warning[800]};`;
      case 'X':
        return `background: ${colors.neutral[100]}; color: ${colors.semantic.text.secondary};`;
      case '인스타그램':
        return `background: ${colors.error[100]}; color: ${colors.error[700]};`;
      case '유튜브':
        return `background: ${colors.error[50]}; color: ${colors.error[600]};`;
      default:
        return `background: ${colors.secondary[100]}; color: ${colors.semantic.text.secondary};`;
    }
  }}
`;

// 수집량/분석량 스타일
const CountText = styled.span<{ count: number }>`
  font-weight: 500;
  color: ${props =>
    props.count > 0 ? colors.success[600] : colors.semantic.text.tertiary};
`;

// 빈 상태 메시지 스타일
const EmptyMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${colors.semantic.text.tertiary};
  font-size: 16px;
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.neutral[100]};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 페이지네이션 컨테이너 스타일
const PaginationContainer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.secondary};
`;

// 모바일에서만 테이블 좌우 스크롤 래퍼
const MobileTableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    table {
      min-width: 600px;
      width: 100%;
    }
    th, td {
      white-space: nowrap;
    }
  }
`;

// 키워드 설정 테이블 Props 인터페이스
interface KeywordSettingTableProps {
  userIdx: number;
}

// 키워드 설정 테이블 컴포넌트
const KeywordSettingTable: React.FC<KeywordSettingTableProps> = ({ userIdx }) => {
  // 상태 관리
  const [keywordSettings, setKeywordSettings] = useState<KeywordSetting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [limit] = useState<number>(15); // 고정값으로 설정
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordSetting | null>(null);
  const [isDataModalOpen, setIsDataModalOpen] = useState<boolean>(false);

  // 키워드 설정 로드
  const loadKeywordSettings = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: KeywordSettingParams = {
        userIdx,
        page,
        limit
      };
      
      // 실제 API 호출 시도, 실패하면 목업 데이터 사용
      let response;
      try {
        response = await CustomerService.getKeywordSettings(params);
        console.log(`✅ 고객(${userIdx}) 키워드 설정 API 성공:`, response);
      } catch (apiError) {
        setError('키워드 설정을 불러오는데 실패했습니다.');
        console.error(`❌ 고객(${userIdx}) 키워드 설정 로드 실패:`, apiError);
        return;
      }
      
      setKeywordSettings(response.items);
      setCurrentPage(response.page);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / response.limit));
    } catch (err) {
      setError('키워드 설정을 불러오는데 실패했습니다.');
      console.error('Keyword settings loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [userIdx, limit]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadKeywordSettings(1);
  }, [loadKeywordSettings]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    loadKeywordSettings(page);
  };

  // 키워드 수집 데이터 모달 열기
  const handleOpenKeywordData = (keyword: KeywordSetting) => {
    if (!keyword.keyword_idx) {
      console.warn('keyword_idx가 없어 수집 데이터 조회를 할 수 없습니다.', keyword);
      return;
    }
    setSelectedKeyword(keyword);
    setIsDataModalOpen(true);
  };

  // 키워드 수집 데이터 모달 닫기
  const handleCloseKeywordData = () => {
    setIsDataModalOpen(false);
    setSelectedKeyword(null);
  };

  // 숫자 포맷팅
  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  // 활성화 상태 표시명 변환
  const getStatusLabel = (status: string) => {
    return status === 'ON' ? '활성' : '비활성';
  };

  // 로딩 상태
  if (loading) {
    return (
      <TableContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </TableContainer>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <TableContainer>
        <EmptyMessage>{error}</EmptyMessage>
      </TableContainer>
    );
  }

  // 빈 데이터 상태
  if (keywordSettings.length === 0) {
    return (
      <TableContainer>
        <EmptyMessage>키워드 설정이 없습니다.</EmptyMessage>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      {/* 모바일에서만 좌우 스크롤 가능하도록 래퍼로 감쌈 */}
      <MobileTableScrollWrapper>
        <Table>
          <TableHeader>
            <tr>
              <HeaderCell>생성일자</HeaderCell>
              <HeaderCell>타겟사이트</HeaderCell>
              <HeaderCell>브랜드명</HeaderCell>
              <HeaderCell>키워드</HeaderCell>
              <HeaderCell>수집주기</HeaderCell>
              <HeaderCell>분석유형</HeaderCell>
              <HeaderCell>수집량</HeaderCell>
              <HeaderCell>분석량</HeaderCell>
              <HeaderCell>활성화</HeaderCell>
              <HeaderCell>수집 데이터</HeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {keywordSettings.map((keyword, index) => (
              <TableRow key={index}>
                <DataCell>{formatDateTimeKST(keyword.생성일자)}</DataCell>
                <DataCell>
                  <SiteBadge site={keyword.타겟사이트}>
                    {keyword.타겟사이트}
                  </SiteBadge>
                </DataCell>
                <DataCell>{keyword.브랜드명}</DataCell>
                <DataCell>
                  <strong>{keyword.키워드}</strong>
                </DataCell>
                <DataCell>{keyword.수집주기}</DataCell>
                <DataCell>
                  <AnalysisTypeBadge type={keyword.분석유형}>
                    {keyword.분석유형}
                  </AnalysisTypeBadge>
                </DataCell>
                <DataCell>
                  <CountText count={keyword.수집량}>
                    {formatNumber(keyword.수집량)}
                  </CountText>
                </DataCell>
                <DataCell>
                  <CountText count={keyword.분석량}>
                    {formatNumber(keyword.분석량)}
                  </CountText>
                </DataCell>
                <DataCell>
                  <StatusBadge status={keyword.활성화}>
                    {getStatusLabel(keyword.활성화)}
                  </StatusBadge>
                </DataCell>
                <DataCell>
                  <button
                    onClick={() => handleOpenKeywordData(keyword)}
                    disabled={!keyword.keyword_idx}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: `1px solid ${colors.neutral[200]}`,
                      backgroundColor: keyword.keyword_idx
                        ? colors.semantic.background.primary
                        : colors.neutral[100],
                      color: keyword.keyword_idx
                        ? colors.primary[600]
                        : colors.semantic.text.disabled,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: keyword.keyword_idx ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                    }}
                  >
                    수집 데이터 보기
                  </button>
                </DataCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MobileTableScrollWrapper>
      
      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={handlePageChange}
          />
        </PaginationContainer>
      )}

      {/* 키워드 수집 데이터 사이드 패널 */}
      {selectedKeyword && isDataModalOpen && selectedKeyword.keyword_idx && (
        <KeywordDataSidePanel
          userIdx={userIdx}
          keywordIdx={selectedKeyword.keyword_idx}
          keywordText={selectedKeyword.키워드}
          brandName={selectedKeyword.브랜드명}
          targetSite={selectedKeyword.타겟사이트}
          isOpen={isDataModalOpen}
          onClose={handleCloseKeywordData}
        />
      )}
    </TableContainer>
  );
};

export default KeywordSettingTable; 